import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';

const API = '/api';

const Icon = ({ d, size = 16, strokeWidth = 1.75 }) => (
    <svg
        width={size} height={size} viewBox="0 0 24 24"
        fill="none" stroke="currentColor"
        strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round"
    >
        {Array.isArray(d) ? d.map((p, i) => <path key={i} d={p} />) : <path d={d} />}
    </svg>
);

const icons = {
    search: ['M11 19a8 8 0 1 0 0-16 8 8 0 0 0 0 16z', 'M21 21l-4.35-4.35'],
    plus: ['M12 5v14', 'M5 12h14'],
    edit: ['M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7', 'M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z'],
    trash: ['M3 6h18', 'M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2'],
    x: ['M18 6 6 18', 'M6 6l12 12']
};

export default function Passengers({ user }) {
    const [passengers, setPassengers] = useState([]);
    const [clients, setClients] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [modal, setModal] = useState(null);
    const [deleteModal, setDeleteModal] = useState(null);

    const operatorId = user?.operatorId || user?._id || 'all';

    useEffect(() => {
        fetchData();
        fetchClients();
    }, []);

    const fetchClients = async () => {
        try {
            const res = await axios.get(`${API}/clients/${operatorId}`);
            setClients(res.data);
        } catch (err) { console.error(err); }
    };

    const fetchData = async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${API}/passengers/${operatorId}`);
            setPassengers(res.data);
        } catch (err) { console.error(err); }
        finally { setLoading(false); }
    };

    const handleSave = async (formData) => {
        try {
            const payload = { ...formData, operatorId };
            if (modal.data?._id) {
                await axios.put(`${API}/passengers/${modal.data._id}`, payload);
            } else {
                await axios.post(`${API}/passengers`, payload);
            }
            setModal(null);
            fetchData();
        } catch (err) { console.error(err); }
    };

    const handleDelete = async () => {
        try {
            await axios.delete(`${API}/passengers/${deleteModal._id}`);
            setDeleteModal(null);
            fetchData();
        } catch (err) { console.error(err); }
    };

    const filtered = passengers.filter(p =>
        p.name.toLowerCase().includes(search.toLowerCase()) ||
        p.employeeId?.toLowerCase().includes(search.toLowerCase()) ||
        p.clientId?.companyName.toLowerCase().includes(search.toLowerCase())
    );

    return (
        <div className="md-page">
            <div className="md-header">
                <div>
                    <h1 className="md-title">Passengers</h1>
                    <p className="md-subtitle">Manage employee data and their default travel preferences.</p>
                </div>
                <button className="md-add-btn" onClick={() => setModal({ data: null })}>
                    <Icon d={icons.plus} size={14} /> Add Passenger
                </button>
            </div>

            <div className="md-panel">
                <div className="md-panel-toolbar">
                    <div className="md-search-wrap">
                        <Icon d={icons.search} size={14} />
                        <input
                            className="md-input md-search"
                            placeholder="Name, Emp ID, or Client..."
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                        />
                    </div>
                </div>

                {loading ? (
                    <div style={{ padding: 60, textAlign: 'center', color: '#94a3b8' }}>Loading passengers...</div>
                ) : (
                    <div className="md-table-wrap">
                        <table className="md-table">
                            <thead>
                                <tr>
                                    <th className="md-th">Passenger Name</th>
                                    <th className="md-th">Employee ID</th>
                                    <th className="md-th">Client</th>
                                    <th className="md-th">Contact</th>
                                    <th className="md-th">Locations</th>
                                    <th className="md-th">Status</th>
                                    <th className="md-th">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filtered.map(p => (
                                    <tr key={p._id}>
                                        <td className="md-td"><strong>{p.name}</strong></td>
                                        <td className="md-td">{p.employeeId}</td>
                                        <td className="md-td">{p.clientId?.companyName || '—'}</td>
                                        <td className="md-td">{p.contactNumber || p.email}</td>
                                        <td className="md-td">
                                            <div style={{ fontSize: 11 }}>H: {p.pickupLocation || '—'}</div>
                                            <div style={{ fontSize: 11 }}>W: {p.dropLocation || '—'}</div>
                                        </td>
                                        <td className="md-td">
                                            <span className={`md-status-badge ${p.status === 'Active' ? 'md-badge-active' : 'md-badge-inactive'}`}>
                                                {p.status}
                                            </span>
                                        </td>
                                        <td className="md-td">
                                            <div className="md-actions">
                                                <button className="md-icon-btn" onClick={() => setModal({ data: p })}><Icon d={icons.edit} size={14} /></button>
                                                <button className="md-icon-btn md-icon-btn-danger" onClick={() => setDeleteModal(p)}><Icon d={icons.trash} size={14} /></button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Modal */}
            {modal && (
                <PassengerForm
                    data={modal.data}
                    clients={clients}
                    onClose={() => setModal(null)}
                    onSave={handleSave}
                />
            )}

            {/* Delete Confirmation */}
            {deleteModal && (
                <div className="md-modal-overlay" onClick={() => setDeleteModal(null)}>
                    <div className="md-modal" style={{ maxWidth: 400 }} onClick={e => e.stopPropagation()}>
                        <div className="md-modal-header">
                            <h3 className="md-modal-title" style={{ color: '#ef4444' }}>Confirm Delete</h3>
                        </div>
                        <div className="md-modal-body">
                            Are you sure you want to delete passenger "<strong>{deleteModal.name}</strong>"?
                        </div>
                        <div className="md-modal-footer">
                            <button className="md-cancel-btn" onClick={() => setDeleteModal(null)}>Cancel</button>
                            <button className="md-save-btn" style={{ background: '#ef4444' }} onClick={handleDelete}>Delete</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

function PassengerForm({ data, clients, onClose, onSave }) {
    const [form, setForm] = useState(() => {
        if (data) {
            return {
                ...data,
                firstName: data.firstName || data.name?.split(' ')[0] || '',
                lastName: data.lastName || data.name?.split(' ').slice(1).join(' ') || '',
                email: data.email || '',
                contactNumber: data.contactNumber || '',
                clientId: data.clientId?._id || data.clientId || '',
                pickupLocation: data.pickupLocation || '',
                dropLocation: data.dropLocation || '',
                status: data.status || 'Active'
            };
        }
        return {
            firstName: '', lastName: '', name: '', email: '', employeeId: '', contactNumber: '', clientId: '',
            pickupLocation: '', dropLocation: '', status: 'Active'
        };
    });

    const pickupInputRef = useRef(null);
    const dropInputRef = useRef(null);

    useEffect(() => {
        if (!window.google) return;
        const pickupAutocomplete = new window.google.maps.places.Autocomplete(pickupInputRef.current);
        const dropAutocomplete = new window.google.maps.places.Autocomplete(dropInputRef.current);

        pickupAutocomplete.addListener('place_changed', () => {
            const place = pickupAutocomplete.getPlace();
            if (place.formatted_address) setForm(prev => ({ ...prev, pickupLocation: place.formatted_address }));
        });

        dropAutocomplete.addListener('place_changed', () => {
            const place = dropAutocomplete.getPlace();
            if (place.formatted_address) setForm(prev => ({ ...prev, dropLocation: place.formatted_address }));
        });
    }, []);

    const handleLocalSave = () => {
        if (!form.firstName || !form.lastName || !form.email || !form.clientId) {
            alert("Please fill in all required fields (First Name, Last Name, Email, Client)");
            return;
        }
        const finalData = {
            ...form,
            name: `${form.firstName} ${form.lastName}`.trim()
        };
        onSave(finalData);
    };

    return (
        <div className="md-modal-overlay" onClick={onClose}>
            <div className="md-modal" style={{ maxWidth: 600 }} onClick={e => e.stopPropagation()}>
                <div className="md-modal-header">
                    <h3 className="md-modal-title">{data ? 'Update Passenger Record' : 'Register New Passenger'}</h3>
                    <button className="md-modal-close" onClick={onClose}><Icon d={icons.x} size={18} /></button>
                </div>
                <div className="md-modal-body" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                    <div className="md-form-group">
                        <label className="md-label">First Name*</label>
                        <input className="md-input" value={form.firstName} onChange={e => setForm({ ...form, firstName: e.target.value })} placeholder="John" />
                    </div>
                    <div className="md-form-group">
                        <label className="md-label">Last Name*</label>
                        <input className="md-input" value={form.lastName} onChange={e => setForm({ ...form, lastName: e.target.value })} placeholder="Doe" />
                    </div>
                    <div className="md-form-group" style={{ gridColumn: 'span 2' }}>
                        <label className="md-label">Email Address* (for Synchronization)</label>
                        <input className="md-input" type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} placeholder="passenger@company.com" />
                    </div>
                    <div className="md-form-group">
                        <label className="md-label">Contact Number*</label>
                        <input className="md-input" value={form.contactNumber} onChange={e => setForm({ ...form, contactNumber: e.target.value })} placeholder="Primary Phone" />
                    </div>
                    <div className="md-form-group">
                        <label className="md-label">Employee ID / Passenger ID</label>
                        <input className="md-input" value={form.employeeId} onChange={e => setForm({ ...form, employeeId: e.target.value })} placeholder="EMP-123" />
                    </div>
                    <div className="md-form-group" style={{ gridColumn: 'span 2' }}>
                        <label className="md-label">Client Association*</label>
                        <select className="md-input" value={form.clientId} onChange={e => setForm({ ...form, clientId: e.target.value })}>
                            <option value="">Select a Client</option>
                            {clients.map(c => <option key={c._id} value={c._id}>{c.companyName}</option>)}
                        </select>
                    </div>
                    <div className="md-form-group" style={{ gridColumn: 'span 2' }}>
                        <label className="md-label">Default Pickup / Home Location</label>
                        <input ref={pickupInputRef} className="md-input" value={form.pickupLocation} onChange={e => setForm({ ...form, pickupLocation: e.target.value })} placeholder="Search address..." />
                    </div>
                    <div className="md-form-group" style={{ gridColumn: 'span 2' }}>
                        <label className="md-label">Default Drop / Work Location</label>
                        <input ref={dropInputRef} className="md-input" value={form.dropLocation} onChange={e => setForm({ ...form, dropLocation: e.target.value })} placeholder="Search address..." />
                    </div>
                    <div className="md-form-group">
                        <label className="md-label">Status</label>
                        <select className="md-input" value={form.status} onChange={e => setForm({ ...form, status: e.target.value })}>
                            <option value="Active">Active</option>
                            <option value="Inactive">Inactive</option>
                        </select>
                    </div>
                </div>
                <div className="md-modal-footer">
                    <button className="md-cancel-btn" onClick={onClose}>Discard</button>
                    <button className="md-save-btn" onClick={handleLocalSave}>Save Passenger</button>
                </div>
            </div>
        </div>
    );
}
